import React, { useRef } from 'react';
import { Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
};

export const FileUploadZone = ({ file, onFileSelect, onFileRemove }: Props) => {
  const { translations } = useLanguage();
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (validateFile(droppedFile)) {
      onFileSelect(droppedFile);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      onFileSelect(selectedFile);
    }
  };

  const validateFile = (file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(file.type)) {
      toast.error(translations?.fileUploadZone?.excelFileError || 'Please select an Excel file only');
      return false;
    }
    
    return true;
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center
        ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}
        ${file ? 'bg-green-50 border-green-500' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {file ? (
        <div className="space-y-2">
          <div className="text-green-600 font-medium">{file.name}</div>
          <button
            onClick={onFileRemove}
            className="text-sm text-red-500 hover:text-red-600"
          >
            {translations?.fileUploadZone?.removeFile || 'Remove File'}
          </button>
        </div>
      ) : (
        <>
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {translations?.fileUploadZone?.chooseFile || 'Choose File'}
            </button>
            <span className="text-gray-500">
              {translations?.fileUploadZone?.dragAndDrop || ' or drag and drop'}
            </span>
          </div>
          <p className="text-gray-500 text-sm mt-2">
            {translations?.fileUploadZone?.excelOnly || 'Excel only'}
          </p>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xls"
        onChange={handleFileSelect}
      />
    </div>
  );
};